import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Send, Loader2, Keyboard, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SpeakButton from "@/components/SpeakButton";
import { getReflectionVoice, getVoiceLabel } from "@/lib/reflectionVoice";
import { notifyStopAllSpeech } from "@/lib/speechBus";
import { sendMirrorTurn, transcribeAudio } from "@/lib/api";
import { toast } from "sonner";

/**
 * ReflectionThread — the multi-turn Mirror conversation.
 *
 * After the initial reflection is displayed, the journaler may continue
 * the exchange up to three turns per UTC day. The Mirror auto-plays each
 * response in the paired reflection voice. On the third turn the Mirror
 * returns the closing message (backend-authored) and locks the input.
 *
 * Voice input is the primary modality (mic default); the text field is
 * secondary at user discretion.
 */
export default function ReflectionThread({
  promptId,
  entry,
  reflection,
  reflectionVoice,
  autoPlayFirst,
  onDistress,
  onTurnsUpdate,
}) {
  const initialTurns = useMemo(() => entry?.turns || [], [entry]);
  const [turns, setTurns] = useState(initialTurns);
  const [draft, setDraft] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [sending, setSending] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [lastMirrorTurnId, setLastMirrorTurnId] = useState(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    setTurns(entry?.turns || []);
  }, [entry?.id, entry?.turns]);

  // Compute today's remaining allowance client-side (matches backend UTC math).
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const userTurnsToday = useMemo(
    () => turns.filter((t) => t.role === "user" && t.day === today).length,
    [turns, today]
  );
  const remainingToday = Math.max(0, 3 - userTurnsToday);
  const closingTurn = turns.find((t) => t.role === "mirror" && t.closing);
  const closedForToday = !!closingTurn || remainingToday === 0;

  const stopRecording = useCallback(() => {
    try { mediaRef.current?.stop(); } catch { /* recorder already stopped */ }
  }, []);

  const startRecording = useCallback(async () => {
    if (!entry) {
      toast.error("Save the reflection first before continuing the conversation.");
      return;
    }
    if (closedForToday) return;
    // Interrupt any Mirror speech instantly — mic tap == "I want to reply now."
    notifyStopAllSpeech();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setRecording(false);
        if (blob.size < 800) {
          toast.error("That was too short to transcribe.");
          return;
        }
        setTranscribing(true);
        try {
          const text = await transcribeAudio(blob);
          if (text) setDraft((prev) => (prev ? `${prev} ${text}`.trim() : text));
          else toast.error("No speech was heard.");
        } catch (e) {
          console.error(e);
          toast.error("Transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error(e);
      toast.error("Microphone access was denied.");
    }
  }, [entry, closedForToday]);

  const handleSend = useCallback(async () => {
    const message = draft.trim();
    if (!message || !entry) return;
    if (closedForToday) return;
    notifyStopAllSpeech();
    setSending(true);
    try {
      const res = await sendMirrorTurn(entry.id, message);
      const nextTurns = [...turns, res.user_turn, res.mirror_turn];
      setTurns(nextTurns);
      setDraft("");
      setLastMirrorTurnId(res.mirror_turn.id);
      onTurnsUpdate?.(nextTurns);
      if (res.distress?.triggered) {
        notifyStopAllSpeech();
        onDistress?.();
      }
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("You have reached today's three turns. The Mirror rests until tomorrow.");
      } else {
        toast.error("The Mirror could not respond. Try again in a moment.");
      }
    } finally {
      setSending(false);
    }
  }, [draft, entry, turns, closedForToday, onDistress, onTurnsUpdate]);

  const voiceLabel = getVoiceLabel(reflectionVoice || getReflectionVoice());

  if (!reflection) return null;

  return (
    <div data-testid={`mirror-thread-${promptId}`} className="mt-10 space-y-6">
      {/* Initial reflection */}
      <div
        data-testid={`reflection-text-${promptId}`}
        className="border-l-2 border-terracotta pl-6 py-2 animate-fade-up"
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta">The Mirror</p>
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.2em] text-bone-500 mt-1">
              Read back by {voiceLabel}
            </p>
          </div>
          <SpeakButton
            text={reflection}
            voice={reflectionVoice}
            size="sm"
            autoPlay={autoPlayFirst}
            testId={`speak-reflection-${promptId}`}
            label="Read reflection aloud"
          />
        </div>
        <p className="font-serif text-lg text-bone-200 leading-relaxed italic">{reflection}</p>
      </div>

      {/* Multi-turn continuation */}
      {turns.map((t) => {
        const isMirror = t.role === "mirror";
        const isClosing = isMirror && t.closing;
        return (
          <div
            key={t.id}
            data-testid={`mirror-turn-${t.id}`}
            className={
              isMirror
                ? `border-l-2 ${isClosing ? "border-bone-400" : "border-terracotta"} pl-6 py-2 animate-fade-up`
                : "pl-6 py-2 border-l-2 border-obsidian-600 animate-fade-up"
            }
          >
            <div className="flex items-start justify-between gap-4 mb-2">
              <p
                className={`font-sans uppercase tracking-[0.24em] text-[0.6rem] ${
                  isMirror ? (isClosing ? "text-bone-300" : "text-terracotta") : "text-bone-500"
                }`}
              >
                {isMirror ? (isClosing ? "The Mirror · Closing" : "The Mirror") : "You"}
              </p>
              {isMirror && (
                <SpeakButton
                  text={t.text}
                  voice={reflectionVoice}
                  size="sm"
                  autoPlay={t.id === lastMirrorTurnId}
                  testId={`speak-mirror-turn-${t.id}`}
                  label="Read this reply aloud"
                />
              )}
            </div>
            <p
              className={
                isMirror
                  ? "font-serif text-lg text-bone-200 leading-relaxed italic"
                  : "font-serif text-base text-bone-300 leading-relaxed"
              }
              data-testid={isClosing ? `mirror-closing-${promptId}` : undefined}
            >
              {t.text}
            </p>
          </div>
        );
      })}

      {/* Continue input */}
      {closedForToday ? (
        <div
          data-testid={`mirror-remaining-${promptId}`}
          className="mt-6 border border-obsidian-600 bg-obsidian-900/40 px-5 py-4"
        >
          <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-1">
            Today's circle is complete
          </p>
          <p className="font-serif text-bone-300 italic">
            The Mirror rests until tomorrow. Return in a new day to continue this conversation.
          </p>
        </div>
      ) : (
        <div className="mt-6 border border-obsidian-600 bg-obsidian-900/40 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 gap-2">
            <p
              data-testid={`mirror-remaining-${promptId}`}
              className="font-sans uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[0.6rem] text-bone-500"
            >
              {remainingToday} of 3 turns today
            </p>
            <button
              type="button"
              onClick={() => setTextMode((v) => !v)}
              data-testid={`mirror-toggle-input-${promptId}`}
              className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500 hover:text-terracotta transition-colors min-h-[44px] px-1"
              title={textMode ? "Switch to voice" : "Switch to text"}
            >
              {textMode ? <Mic className="h-3 w-3" /> : <Keyboard className="h-3 w-3" />}
              {textMode ? "Voice" : "Type instead"}
            </button>
          </div>

          {textMode && (
            <Textarea
              data-testid={`mirror-turn-input-${promptId}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Speak back, softly."
              rows={3}
              className="bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-serif text-base leading-relaxed rounded-none placeholder:text-bone-500 mb-3"
            />
          )}

          {!textMode && draft && (
            <p className="font-serif text-base text-bone-200 italic mb-3">{draft}</p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {!recording ? (
              <Button
                data-testid={`mirror-turn-mic-${promptId}`}
                onClick={startRecording}
                disabled={transcribing || sending}
                className="rounded-full bg-terracotta hover:bg-terracotta-soft text-bone-100 h-12 w-12 p-0 shrink-0 shadow-[0_0_0_1px_rgba(184,90,68,0.35)] transition-colors"
                title="Speak your reply"
              >
                {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mic className="h-4 w-4" />}
              </Button>
            ) : (
              <Button
                data-testid={`mirror-turn-stop-${promptId}`}
                onClick={stopRecording}
                className="rounded-full bg-terracotta hover:bg-terracotta-deep text-bone-100 h-12 w-12 p-0 shrink-0 animate-breathe transition-colors"
                title="Stop and transcribe"
              >
                <MicOff className="h-4 w-4" />
              </Button>
            )}
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 flex-1 min-w-0">
              {recording ? "Listening…" : transcribing ? "Transcribing…" : "Tap the mic. Speak."}
            </p>
            <Button
              data-testid={`mirror-turn-send-${promptId}`}
              onClick={handleSend}
              disabled={!draft.trim() || sending || recording || transcribing}
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-4 sm:px-5 py-3 sm:py-4 min-h-[48px] transition-colors w-full sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send to the Mirror
            </Button>
          </div>
          {draft && !sending && (
            <button
              type="button"
              onClick={() => setDraft("")}
              className="mt-3 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500 hover:text-terracotta transition-colors min-h-[44px]"
              data-testid={`mirror-turn-clear-${promptId}`}
            >
              Clear draft
            </button>
          )}
        </div>
      )}
    </div>
  );
}
